import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ExternalLink, Bell } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { useTranslation, translations } from '@/components/utils/translations';

// [2026-10-29] i18n: the type badge is translated only outside English (English keeps the raw
// `signal_type` text, CSS-capitalised, exactly as before). Keys mirror pages/Signals.jsx
// SIGNAL_TYPE_KEYS (sigType + PascalCase); an unknown type falls back to the raw text.
function signalTypeKey(raw) {
  if (!raw) return null;
  const key = 'sigType' + String(raw).split('_').filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('');
  return translations.en && Object.prototype.hasOwnProperty.call(translations.en, key) ? key : null;
}

export default function AccountSignalList({ signals, currentTenant, account }) {
  const contextUser = useUser();
  const { t, language } = useTranslation();
  console.log('🔍 AccountSignalList: Rendering with:', {
    signalsCount: signals?.length || 0,
    currentTenant,
    accountId: account?.account_id || account?.id,
    accountName: account?.account_name
  });

  // Error boundary - catch any rendering errors
  try {
    if (!signals || signals.length === 0) {
      console.log('📊 AccountSignalList: No signals to display');
      return <p className="text-center text-sm text-[#5C6B7A]">{t('acctNoSignalsForAccount')}</p>;
    }

    const getImpactBadgeClass = (score) => {
      if (score > 0) return "bg-green-100 text-green-700";
      if (score === 0) return "bg-yellow-100 text-yellow-700";
      return "bg-red-100 text-red-700";
    };

    // Get effective tenant - admin-gated: only admins may use admin-active-tenant
    const adminTenant =
      contextUser?.role === 'admin'
        ? localStorage.getItem('admin-active-tenant')
        : null;
    const effectiveTenant = resolveEffectiveTenant(
      adminTenant,
      currentTenant,
      contextUser,
      'instructure'
    );
    const isCalmedicaTenant = effectiveTenant === 'calmedica' || (account?.tenant_id === 'calmedica');

    console.log('📊 AccountSignalList: Processing signals:', {
      totalSignals: signals.length,
      effectiveTenant,
      isCalmedicaTenant
    });

    return (
      <div className="space-y-3 md:space-y-4">
        {signals.map((signal, index) => {
          try {
            // Validate signal data
            if (!signal || !signal.id) {
              console.warn(`⚠️ AccountSignalList: Invalid signal at index ${index}:`, signal);
              return null;
            }

            // Safely parse signal date
            let formattedDate = 'Date unknown';
            try {
              if (signal.signal_date) {
                formattedDate = format(new Date(signal.signal_date), 'dd MMM yyyy');
              }
            } catch (dateError) {
              console.warn(`⚠️ AccountSignalList: Error formatting date for signal ${signal.id}:`, dateError);
            }

            return (
              <Card key={signal.id} className="border-0 shadow-md">
                <CardContent className="p-3 md:p-4">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      {signal.source_url ? (
                        <a 
                          href={signal.source_url} 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[#00C2C2] hover:text-[#00A3A3] text-xs md:text-sm leading-tight inline-flex items-center gap-1 hover:underline"
                        >
                          {signal.title || t('acctUntitledSignal')}
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      ) : (
                        <p className="font-semibold text-[#0A1A2F] text-xs md:text-sm leading-tight">{signal.title || t('acctUntitledSignal')}</p>
                      )}
                      <p className="text-[10px] md:text-sm text-[#5C6B7A] mt-1 line-clamp-2">{signal.description || t('acctNoDescriptionAvailable')}</p>
                    </div>
                    {signal.source_url && (
                      <a href={signal.source_url} target="_blank" rel="noopener noreferrer" className="p-1.5 md:p-2 rounded-md text-[#5C6B7A] hover:bg-gray-100 flex-shrink-0">
                        <ExternalLink className="w-3 h-3 md:w-4 md:h-4"/>
                      </a>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2 md:mt-3 gap-2 flex-wrap">
                    <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                      <Badge variant="outline" className={`${language === 'en' ? 'capitalize ' : ''}text-[9px] md:text-xs px-1.5 md:px-2`}>
                        {(language !== 'en' && signalTypeKey(signal.signal_type)) ? t(signalTypeKey(signal.signal_type)) : (signal.signal_type?.replace('_', ' ') || t('acctSignalUpdate'))}
                      </Badge>
                      {signal.impact_score !== undefined && signal.impact_score !== null && (
                        <Badge className={`${getImpactBadgeClass(signal.impact_score)} text-[9px] md:text-xs px-1.5 md:px-2`}>
                          {t('acctImpactLabel')} {signal.impact_score > 0 ? `+${signal.impact_score}` : signal.impact_score}
                        </Badge>
                      )}
                      {isCalmedicaTenant && signal.department && (
                        <Badge className="bg-purple-100 text-purple-700 text-[9px] md:text-xs px-1.5 md:px-2">
                          {signal.department}
                        </Badge>
                      )}
                      {signal.tags && Array.isArray(signal.tags) && signal.tags.length > 0 && 
                        signal.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="text-[9px] md:text-xs px-1.5 md:px-2">
                            {tag}
                          </Badge>
                        ))
                      }
                    </div>
                    <p className="text-[9px] md:text-xs text-[#5C6B7A] whitespace-nowrap">{formattedDate}</p>
                  </div>
                </CardContent>
              </Card>
            );
          } catch (signalError) {
            console.error(`❌ AccountSignalList: Error rendering signal at index ${index}:`, signalError, signal);
            return (
              <Card key={`error-${index}`} className="border-0 shadow-md border-red-200">
                <CardContent className="p-3 md:p-4">
                  <p className="text-sm text-red-600">Error displaying signal</p>
                </CardContent>
              </Card>
            );
          }
        })}
      </div>
    );
  } catch (error) {
    console.error('❌ AccountSignalList: Fatal error rendering component:', error);
    console.error('Error details:', {
      errorMessage: error.message,
      errorStack: error.stack,
      signals: signals,
      currentTenant,
      account
    });
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm font-semibold text-red-800 mb-2">Error loading signals</p>
        <p className="text-xs text-red-600">Please refresh the page or try again later.</p>
        <details className="mt-2 text-xs text-red-500">
          <summary className="cursor-pointer">Error details</summary>
          <pre className="mt-2 whitespace-pre-wrap">{error.message}</pre>
        </details>
      </div>
    );
  }
}